import React from 'react'
import { useQuery } from '@apollo/client'
import { Banner } from '@careguide/banners'
import { CreditPeriodEnum } from 'graph/globalTypes'
import Button from 'components/generic/Button'

import { CreditsBanner as CreditsBannerData, CreditsBannerVariables } from 'graph/CreditsBanner'
import { capitalizeAllWords } from 'utils/generalUtils'
import { ConversationIcon } from './ConversationIcon'
import CREDITS_USED from './query.CreditsBanner.gql'

export type CreditsBannerProps = {
  isProvider: boolean
  credits: number
  period: CreditPeriodEnum
  personName?: string
  onClose?(): void
}

export const CreditsBanner = ({
  isProvider,
  credits,
  period,
  personName,
  onClose,
}: CreditsBannerProps) => {
  const { data, error, loading } = useQuery<CreditsBannerData, CreditsBannerVariables>(
    CREDITS_USED,
    {
      fetchPolicy: 'network-only',
      variables: { period },
    }
  )

  if (error || loading || !data) return null

  const creditsUsed = data.user?.creditsUsed || 0
  const creditsAvailable = Math.max(credits - creditsUsed, 0)

  const creditName = isProvider ? 'application' : 'credit'
  const person = isProvider ? 'client' : data.site?.providerName
  const creditsReset = period === 'week' ? 'on Monday' : 'tomorrow'

  if (!creditsAvailable) {
    return (
      <ZeroCreditsBanner
        creditName={creditName}
        isProvider={isProvider}
        person={person!}
        credits={credits}
        period={period}
        creditsReset={creditsReset}
        onClose={onClose}
      />
    )
  }

  return (
    <HasCreditsBanner
      credits={credits}
      creditsAvailable={creditsAvailable}
      creditName={creditName}
      person={person!}
      period={period}
      creditsReset={creditsReset}
      personName={personName}
      onClose={onClose}
    />
  )
}

type ZeroCreditsBannerProps = {
  credits: number
  creditName: string
  isProvider: boolean
  person: string
  period: string
  creditsReset: string
  onClose?(): void
}

const ZeroCreditsBanner = ({
  credits,
  creditName,
  isProvider,
  person,
  period,
  creditsReset,
  onClose,
}: ZeroCreditsBannerProps) => {
  const heading = isProvider
    ? `You have reached your application limit.`
    : `You have no credits available.`

  return (
    <Banner
      image={<ConversationIcon />}
      heading={heading}
      subheading={`${capitalizeAllWords(creditName)}s reset ${creditsReset}.`}
      body={
        `Your Basic Plan is limited to contacting ${credits} ` +
        `${pluralFor(person, credits)} every ${period}. Upgrade for unlimited access.`
      }
      button={<Button href='/upgrade'>Upgrade</Button>}
      onClose={onClose}
    />
  )
}

type HasCreditsBannerProps = {
  credits: number
  creditsAvailable: number
  creditName: string
  person: string
  period: string
  creditsReset: string
  personName?: string
  onClose?(): void
}

const HasCreditsBanner = ({
  credits,
  creditsAvailable,
  creditName,
  person,
  period,
  creditsReset,
  personName,
  onClose,
}: HasCreditsBannerProps) => {
  const body = personName ? `Contact ${personName} today.` : `Send a message or call today.`
  return (
    <Banner
      image={<ConversationIcon />}
      heading={`You have ${creditsAvailable} free ${pluralFor(creditName, creditsAvailable)}`}
      subheading={`${capitalizeAllWords(creditName)}s reset ${creditsReset}.`}
      body={
        `Your Basic Plan lets you reach out to ${credits} ` +
        `${pluralFor(person, credits)} every ${period}. ` +
        body
      }
      onClose={onClose}
    />
  )
}

const pluralFor = (word: string, count: number) => {
  if (count < 2) return word
  return word + 's'
}
